import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Mail, Phone } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Contact = ({ isFullPage = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to submit form');
      }
      
      // Show success message
      toast.success('Message sent successfully! We\'ll be in touch soon.');
      
      // Reset form
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error(error.message || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Full page dark contact
  if (isFullPage) {
    return (
      <div className="contact-page-wrapper">
        <section className="contact-hero">
          <div className="contact-hero-overlay"></div>
          <div className="contact-hero-content">
            <h1 className="contact-hero-title">Contact Us</h1>
            <p className="contact-hero-subtitle">Let&apos;s start a conversation about your project</p>
          </div>
        </section>
        <section className="contact-form-section" ref={sectionRef}>
          <div className="contact-form-container">
            <div className={`contact-content contact-content-dark ${isVisible ? 'contact-visible' : ''}`}>
              <h2 className="section-title-inline contact-form-title">
                <span className="title-light">GET IN</span> <span className="title-bold">TOUCH.</span>
              </h2>
              <form onSubmit={handleSubmit} className="contact-form-dark">
                <div className="contact-form-row">
                  <Input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="contact-input-dark"
                    disabled={isSubmitting}
                  />
                  <Input
                    type="email"
                    name="email"
                    placeholder="Your Email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="contact-input-dark"
                    disabled={isSubmitting}
                  />
                </div>
                <Textarea
                  name="message"
                  placeholder="Tell us about your project"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  className="contact-textarea-dark"
                  rows={5}
                  disabled={isSubmitting}
                />
                <Button type="submit" className="contact-submit-dark" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
              <div className="contact-details-footer contact-details-dark">
                <span className="contact-details-label">Or reach us directly</span>
                <div className="contact-details-links">
                  <a href="mailto:enquiries@domusliving.net" className="contact-detail-link">
                    <Mail size={16} strokeWidth={1.5} />
                    <span>enquiries@domusliving.net</span>
                  </a>
                  <span className="contact-detail-divider">|</span>
                  <a href="tel:0417012601" className="contact-detail-link">
                    <Phone size={16} strokeWidth={1.5} />
                    <span>0417 012 601</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Homepage contact section (light theme)
  return (
    <section id="contact" className="home-section home-section-cream" ref={sectionRef}>
      <div className="home-section-inner">
        <div className="home-section-header">
          <h2 className="section-title-inline">
            <span className="title-light">GET IN</span> <span className="title-bold">TOUCH.</span>
          </h2>
        </div>
        <div className={`contact-content ${isVisible ? 'contact-visible' : ''}`}>
          <p className="contact-intro-text">
            We&apos;d love to hear about your project. Let&apos;s start a conversation.
          </p>
          <form onSubmit={handleSubmit} className="contact-form-new">
            <div className="contact-form-row">
              <Input
                type="text"
                name="name"
                placeholder="Your Name"
                value={formData.name}
                onChange={handleChange}
                required
                className="contact-input-new"
                disabled={isSubmitting}
              />
              <Input
                type="email"
                name="email"
                placeholder="Your Email"
                value={formData.email}
                onChange={handleChange}
                required
                className="contact-input-new"
                disabled={isSubmitting}
              />
            </div>
            <Textarea
              name="message"
              placeholder="Tell us about your project"
              value={formData.message}
              onChange={handleChange}
              required
              className="contact-textarea-new"
              rows={5}
              disabled={isSubmitting}
            />
            <Button type="submit" className="contact-submit-btn" disabled={isSubmitting}>
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </Button>
          </form>
          <div className="contact-details-footer">
            <span className="contact-details-label">Or reach us directly</span>
            <div className="contact-details-links">
              <a href="mailto:enquiries@domusliving.net" className="contact-detail-link">
                <Mail size={16} strokeWidth={1.5} />
                <span>enquiries@domusliving.net</span>
              </a>
              <span className="contact-detail-divider">|</span>
              <a href="tel:0417012601" className="contact-detail-link">
                <Phone size={16} strokeWidth={1.5} />
                <span>0417 012 601</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
